import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { subDays, format, parseISO } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useMediaQuery } from 'react-responsive';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  Box, Container, Typography, Paper, Grid, CircularProgress,
  Button, Alert, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, ToggleButtonGroup, ToggleButton, IconButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import SpeedIcon from '@mui/icons-material/Speed';
import CloseIcon from '@mui/icons-material/Close';

import { useUserAuth } from '../../context/UserAuthContext';
import Header from '../../components/Header/Header';
import Logo from '../../components/Logo/Logo';

const TRACKING_START = 'June 16, 2026';
const EXCLUDED_SITES = ['999 - Lab @ Base Camp'];

function fmtPct(rate) {
  if (rate === null || rate === undefined) return '—';
  return `${(rate * 100).toFixed(1)}%`;
}

function fmtBucket(val, granularity) {
  if (!val) return '';
  try {
    const d = typeof val === 'string' ? parseISO(val) : new Date(val);
    return granularity === 'hour' ? format(d, 'MMM d HH:mm') : format(d, 'MMM d');
  } catch {
    return String(val);
  }
}

function sortRows(rows, orderBy, order) {
  return [...rows].sort((a, b) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
  });
}

function KpiCard({ label, value, sub }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.2, mt: 0.5 }}>
        {value ?? '—'}
      </Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

function DrillDown({ row, from, to, token, onClose }) {
  const [granularity, setGranularity] = useState('day');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (gran) => {
    if (!token || !row) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ site: row.site, from, to, granularity: gran });
      const { data: rows } = await axios.get(`/hd-api/rush-usage/timeseries?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(rows.map(r => ({
        ...r,
        rushRatePct: r.rushRate != null ? +(r.rushRate * 100).toFixed(1) : 0,
      })));
    } catch (e) {
      setError('Failed to load time-series data.');
    } finally {
      setLoading(false);
    }
  }, [token, row, from, to]);

  useEffect(() => { load(granularity); }, [load, granularity]);

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {row.siteName} — Rush Button Usage Over Time
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={granularity}
            exclusive
            size="small"
            onChange={(_, v) => { if (v) setGranularity(v); }}
          >
            <ToggleButton value="day">Day</ToggleButton>
            <ToggleButton value="hour">Hour</ToggleButton>
          </ToggleButtonGroup>
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && data && data.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No data for this period.
        </Typography>
      )}
      {!loading && !error && data && data.length > 0 && (
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={data} margin={{ top: 4, right: 24, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis
              dataKey="bucket"
              tickFormatter={v => fmtBucket(v, granularity)}
              tick={{ fontSize: 11 }}
            />
            <YAxis yAxisId="orders" orientation="left" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="rate" orientation="right" tickFormatter={v => `${v}%`} tick={{ fontSize: 11 }} domain={[0, 100]} />
            <Tooltip
              labelFormatter={v => fmtBucket(v, granularity)}
              formatter={(v, name) => {
                if (name === 'Rush Rate %') return [`${v}%`, name];
                return [v, name];
              }}
            />
            <Legend />
            <Bar yAxisId="orders" dataKey="totalOrders" name="Total Orders" fill="#5BADD6" maxBarSize={40} />
            <Bar yAxisId="orders" dataKey="rushedOrders" name="Rushed Orders" fill="#163350" maxBarSize={40} />
            <Line yAxisId="rate" type="monotone" dataKey="rushRatePct" name="Rush Rate %" stroke="#e65100" strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );
}

const COLUMNS = [
  { id: 'siteName', label: 'Store', align: 'left' },
  { id: 'totalOrders', label: 'Total Orders', align: 'right' },
  { id: 'rushedOrders', label: 'Rushed Orders', align: 'right' },
  { id: 'rushRate', label: 'Rush Rate', align: 'right' },
];

export default function RushUsageDashboard() {
  const { user: firebaseUser } = useUserAuth();
  const isTabletOrMobile = useMediaQuery({ maxWidth: 1224 });

  const [from, setFrom] = useState(subDays(new Date(), 30));
  const [to, setTo] = useState(new Date());
  const [appliedFrom, setAppliedFrom] = useState(subDays(new Date(), 30));
  const [appliedTo, setAppliedTo] = useState(new Date());

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [token, setToken] = useState(null);

  const [orderBy, setOrderBy] = useState('siteName');
  const [order, setOrder] = useState('asc');

  const getToken = useCallback(async () => {
    if (!firebaseUser?.getIdToken) return null;
    return firebaseUser.getIdToken(true);
  }, [firebaseUser]);

  const loadSummary = useCallback(async (f, t) => {
    const tok = await getToken();
    if (!tok) return;
    setToken(tok);
    setLoading(true);
    setError(null);
    setSelectedRow(null);
    try {
      const params = new URLSearchParams({
        from: format(f, 'yyyy-MM-dd'),
        to: format(t, 'yyyy-MM-dd'),
      });
      const { data } = await axios.get(`/hd-api/rush-usage/summary?${params}`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setSummary(data.filter(r => !EXCLUDED_SITES.includes(r.siteName)));
    } catch (e) {
      setError('Failed to load rush usage data.');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { loadSummary(appliedFrom, appliedTo); }, [loadSummary, appliedFrom, appliedTo]);

  function handleApply() {
    setAppliedFrom(from);
    setAppliedTo(to);
  }

  function handleSort(col) {
    if (orderBy === col) {
      setOrder(o => o === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(col);
      setOrder('asc');
    }
  }

  const fleetTotalOrders = summary ? summary.reduce((s, r) => s + r.totalOrders, 0) : 0;
  const fleetRushedOrders = summary ? summary.reduce((s, r) => s + r.rushedOrders, 0) : 0;
  const fleetRushRate = fleetTotalOrders ? fleetRushedOrders / fleetTotalOrders : 0;

  const sortedRows = summary ? sortRows(summary, orderBy, order) : [];

  const fromStr = format(appliedFrom, 'yyyy-MM-dd');
  const toStr = format(appliedTo, 'yyyy-MM-dd');

  return (
    <div>
      <Header isTabletOrMobile={isTabletOrMobile} />
      <Container maxWidth="xl">
        <Logo isTabletOrMobile={isTabletOrMobile} />

        <Box sx={{ mt: 2 }}>
          <Paper
            elevation={0}
            sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', mb: 2 }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderLeft: (theme) => `4px solid ${theme.palette.secondary.main}`,
                px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2,
              }}
            >
              <Box
                sx={{
                  backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.1),
                  borderRadius: 2, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <SpeedIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: 'Lato', color: 'text.primary' }}>
                  Rush Button Usage
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Konducter manual release events by store
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Filter bar */}
          <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2, p: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <DatePicker
                selected={from}
                onChange={d => setFrom(d)}
                selectsStart
                startDate={from}
                endDate={to}
                dateFormat="MMM d, yyyy"
                customInput={<TextField size="small" label="From" sx={{ width: 150 }} autoComplete="off" />}
              />
              <DatePicker
                selected={to}
                onChange={d => setTo(d)}
                selectsEnd
                startDate={from}
                endDate={to}
                minDate={from}
                dateFormat="MMM d, yyyy"
                customInput={<TextField size="small" label="To" sx={{ width: 150 }} autoComplete="off" />}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleApply}
                disabled={loading}
                startIcon={<RefreshIcon />}
              >
                Apply
              </Button>
            </Box>
          </Paper>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

          {/* KPI cards */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={4}>
              <KpiCard label="Total Orders" value={fleetTotalOrders.toLocaleString()} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <KpiCard label="Rushed Orders" value={fleetRushedOrders.toLocaleString()} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <KpiCard label="Rush Rate" value={fmtPct(fleetRushRate)} />
            </Grid>
          </Grid>

          {/* Store table */}
          <Paper variant="outlined">
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 520 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {COLUMNS.map(col => (
                        <TableCell key={col.id} align={col.align}>
                          <TableSortLabel
                            active={orderBy === col.id}
                            direction={orderBy === col.id ? order : 'asc'}
                            onClick={() => handleSort(col.id)}
                          >
                            {col.label}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary" py={4}>
                            No data for the selected period.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : sortedRows.map((row) => (
                      <TableRow
                        key={row.site}
                        hover
                        selected={selectedRow?.site === row.site}
                        onClick={() => setSelectedRow(selectedRow?.site === row.site ? null : row)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{row.siteName}</TableCell>
                        <TableCell align="right">{row.totalOrders.toLocaleString()}</TableCell>
                        <TableCell align="right">{row.rushedOrders.toLocaleString()}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            {fmtPct(row.rushRate)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* Drill-down */}
          {selectedRow && token && (
            <DrillDown
              row={selectedRow}
              from={fromStr}
              to={toStr}
              token={token}
              onClose={() => setSelectedRow(null)}
            />
          )}
        </Box>
      </Container>
    </div>
  );
}
